import classnames from 'classnames'
import React, { MouseEventHandler, useCallback, useEffect, useRef, useState } from 'react'
import {
  Row,
  TableInstance,
  TableOptions,
  TableRowProps,
  TableState,
  useSortBy,
  useTable,
} from 'react-table'
import styled, { css } from 'styled-components'

import { EmptyObject } from '../utils/types'
import SortedColumnIcon from './SortedColumnIcon'

export interface RowAsideProps<TData extends EmptyObject> {
  row: Row<TData>
}

export interface TableProps<TData extends EmptyObject> {
  className?: string
  columns: TableOptions<TData>['columns']
  data: TableOptions<TData>['data']
  // initialSortBy: UseSortByState<TData>['sortBy']
  initialSortBy: TableState<TData>['sortBy']
  /**
   * Row "aside" - content displayed immediately following a row
   */
  rowAside?: React.ComponentType<RowAsideProps<TData>>
  showHeaders?: boolean
}

const cellPaddingStyles = css`
  padding-left: 4px;
  padding-right: 4px;

  &:first-of-type {
    padding-left: 10px;
  }

  &:last-of-type {
    padding-right: 10px;
  }
`

export const StyledTableBody = styled.tbody`
  background-color: ${({ theme }) => theme.colors.grayscale.white};
`

export const StyledTableCell = styled.td`
  ${cellPaddingStyles};
  padding-bottom: 8px;
  padding-top: 8px;
  vertical-align: middle;
  word-break: break-word;
`

export const StyledTableRow = styled.tr`
  outline: none;
  vertical-align: middle;
  border-bottom: 1px solid ${({ theme }) => theme.colors.coolGray.cool250};

  &:last-of-type,
  &.hasAside {
    border-bottom: none;
  }

  &,
  ${StyledTableCell} {
    height: 60px;
  }
`

export const StyledTableRowAside = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.coolGray.cool250};
  outline: none;

  &:last-of-type {
    border-bottom: none;
  }
`

export const StyledTableHeaderCell = styled.th`
  ${cellPaddingStyles};
  vertical-align: middle;
`

export const StyledTableHeader = styled.thead`
  vertical-align: middle;

  &,
  ${StyledTableHeaderCell} {
    height: 50px;
  }
`

const cancelTimeout = (ref: React.MutableRefObject<number | undefined>): void => {
  const currentTimeout = ref.current
  if (currentTimeout) {
    window.clearTimeout(currentTimeout)
  }
  ref.current = undefined
}

const TableRowWithAside = <TData extends EmptyObject>({
  row,
  rowAside,
  rowProps,
  visibleColumns,
}: Pick<TableInstance<TData>, 'visibleColumns'> & {
  row: TableInstance<TData>['rows'][number]
  rowAside: TableProps<TData>['rowAside']
  rowProps: Omit<TableRowProps, 'key'>
}) => {
  const [hover, setHover] = useState(false)
  // const [focusWithin, setFocusWithin] = useState(false)
  const hoverTimeout = useRef<number | undefined>()
  const onMouseEnter: MouseEventHandler<HTMLTableRowElement> = useCallback((_e) => {
    cancelTimeout(hoverTimeout)
    setHover(true)
  }, [])

  const onMouseLeave: MouseEventHandler<HTMLTableRowElement> = useCallback((_e) => {
    cancelTimeout(hoverTimeout)
    hoverTimeout.current = window.setTimeout(() => setHover(false), 60)
  }, [])

  useEffect(() => {
    return () => {
      // cleanup on unmount
      cancelTimeout(hoverTimeout)
    }
  }, [])

  const rowAsideElement = rowAside ? React.createElement(rowAside, { row }) : undefined
  return (
    <React.Fragment>
      <StyledTableRow
        className={classnames({ hasAside: !!rowAsideElement, hover })}
        {...rowProps}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {row.cells.map((cell) => {
          const { key: cellKey, ...otherCellProps } = cell.getCellProps()
          return (
            <StyledTableCell
              key={cellKey}
              {...otherCellProps}
              className={cell.column.className}
              rowSpan={rowAsideElement && cell.column.id === 'tracking' ? 2 : 1}
            >
              {cell.render('Cell')}
            </StyledTableCell>
          )
        })}
      </StyledTableRow>
      {rowAsideElement && (
        <StyledTableRowAside
          className={classnames({ hover })}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <StyledTableCell colSpan={visibleColumns.length - 1}>{rowAsideElement}</StyledTableCell>
        </StyledTableRowAside>
      )}
    </React.Fragment>
  )
}

function Table<TData extends Record<string, unknown>>({
  className,
  columns,
  data,
  initialSortBy,
  rowAside,
  showHeaders,
}: TableProps<TData>): JSX.Element {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, visibleColumns } =
    useTable(
      {
        columns,
        data,
        disableSortRemove: true,
        initialState: {
          sortBy: initialSortBy,
        },
      },
      useSortBy,
    )

  return (
    <table {...getTableProps()} className={className}>
      {showHeaders && (
        <StyledTableHeader>
          {headerGroups.map((headerGroup) => {
            const { key: headerGroupKey, ...otherHeaderGroupProps } =
              headerGroup.getHeaderGroupProps()
            return (
              <StyledTableRow key={headerGroupKey} {...otherHeaderGroupProps}>
                {headerGroup.headers.map((column) => {
                  const { key: headerKey, ...otherHeaderProps } = column.getHeaderProps(
                    column.getSortByToggleProps(),
                  )
                  return (
                    <StyledTableHeaderCell
                      key={headerKey}
                      {...otherHeaderProps}
                      className={column.className}
                    >
                      {column.render('Header')}
                      <SortedColumnIcon column={column} />
                    </StyledTableHeaderCell>
                  )
                })}
              </StyledTableRow>
            )
          })}
        </StyledTableHeader>
      )}
      <StyledTableBody {...getTableBodyProps()}>
        {rows.map((row) => {
          prepareRow(row)
          const { key: rowKey, ...otherRowProps } = row.getRowProps()
          return (
            <TableRowWithAside
              key={rowKey}
              visibleColumns={visibleColumns}
              row={row}
              rowAside={rowAside}
              rowProps={otherRowProps}
            />
          )
        })}
      </StyledTableBody>
    </table>
  )
}

const StyledTable = styled(Table)(({ theme }) => ({
  textAlign: 'left',
  borderCollapse: 'collapse',
  borderSpacing: '0',
  a: {
    color: theme.colors.grayscale.black,
    textDecoration: 'underline',
  },
  [`&, ${StyledTableBody}`]: {
    padding: '0',
    width: '100%',
  },
  '.itemsCount': {
    whiteSpace: 'nowrap',
  },
  '.desktopOnly': {
    display: 'none',
  },
  '@media(min-width: 992px)': {
    '.desktopOnly': {
      display: 'revert',
    },
  },
}))

export default StyledTable
